import { Component, OnInit, ViewChild, ElementRef, AfterViewInit, Input, Output, EventEmitter, OnChanges, OnDestroy, NgZone, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { Subscription } from 'rxjs';
import { NearbySchoolsActions } from '../nearby-schools.actions';
import { NearbySchoolsState } from '../nearby-schools.state';
import { UntypedFormGroup, AbstractControl } from '@angular/forms';
import { IApplicationContent, INearbySchool } from '../../types';
import { GMapsCustomPopup } from '../../helpers/gmaps-custom-popup-helper';
import { RootState } from '../../store/root.state';
import { APP_CONFIG, IAppSettings } from '@nha/config/interfaces/iapp-settings.interface';

@Component({
    selector: 'app-nearby-schools-map',
    templateUrl: './nearby-schools-map.component.html',
    styleUrls: ['./nearby-schools-map.component.scss'],
    standalone: true
})
export class NearbySchoolsMapComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
	appSettings = inject<IAppSettings>(APP_CONFIG);

	@Input() nearbySchools: AbstractControl[];
	@Input() guardianHouseholdLatLng: { lat: number; lng: number };
	@Output() addedToApplication: EventEmitter<number>;

	public schools: INearbySchool[];
	private selectedSchoolID: number;
	public googleMap: google.maps.Map;
	public mapMarkers: google.maps.Marker[];
	private currentPopup: GMapsCustomPopup;
	private currentPopupEventSub: Subscription;
	private selecedSchoolIDSub: Subscription;
	private formChangeListenerSubs: Subscription[];
	public appContent: IApplicationContent;

	@ViewChild('googleMapEl') googleMapElement: ElementRef;
	@ViewChild('addbtn') addBtn: ElementRef;

	constructor(private store: Store, public zone: NgZone) {
		this.addedToApplication = new EventEmitter<number>();
	}

	ngOnInit(): void {
		this.schools = this.nearbySchools.map((sc) => sc.value);
		this.appContent = this.store.selectSnapshot(RootState.content);
		this.selecedSchoolIDSub = this.store.select(NearbySchoolsState.selectedSchoolID).subscribe((schoolID) => {
			this.selectedSchoolID = schoolID;

			if (this.schools && this.mapMarkers && this.mapMarkers.length > 0) {
				if (this.selectedSchoolID) {
					this.onMarkerSelect(this.selectedSchoolID);
					this.panToSchool(this.selectedSchoolID);
				} else {
					this.onMarkerSelect(this.selectedSchoolID);
				}
			}
		});
	}

	ngAfterViewInit() {
		this.buildMap();
	}

	ngOnChanges() {
		this.schools = this.nearbySchools.map((sc) => sc.value);

		if (this.formChangeListenerSubs) {
			this.formChangeListenerSubs.forEach((s) => s.unsubscribe());
		}

		// Angular does not seem to trigger a change event when updating a schools isApplying status
		// Refactor in the future if possible
		this.formChangeListenerSubs = this.nearbySchools.map((ns) => {
			const ctrls = (ns as UntypedFormGroup).controls;

			return ctrls.isApplying.valueChanges.subscribe((x) => {
				this.schools.find((s) => s.schoolID === ctrls.schoolID.value).isApplying = x;
				this.setMarkers();
				this.showPopUp(this.selectedSchoolID);
			});
		});

		if (this.schools && this.googleMap) {
			this.setMarkers();
			this.setBounds();
		}

		if (this.selectedSchoolID) {
			this.showPopUp(this.selectedSchoolID);
		}
	}

	ngOnDestroy() {
		if (this.currentPopupEventSub) {
			this.currentPopupEventSub.unsubscribe();
		}
		if (this.selecedSchoolIDSub) {
			this.selecedSchoolIDSub.unsubscribe();
		}
		if (this.formChangeListenerSubs) {
			this.formChangeListenerSubs.forEach((s) => s.unsubscribe());
		}
	}

	buildMap() {
		this.googleMap = new google.maps.Map(this.googleMapElement.nativeElement, {
			center: this.guardianHouseholdLatLng,
			fullscreenControl: false,
			streetViewControl: false,
			mapTypeControl: false,
			zoom: 8
		});

		this.setMarkers();
		this.setBounds();
	}

	setMarkers() {
		if (this.mapMarkers && this.mapMarkers.length > 0) {
			this.mapMarkers.forEach((m) => m.setMap(null));
		}

		this.mapMarkers = [
			new google.maps.Marker({
				position: this.guardianHouseholdLatLng,
				map: this.googleMap,
				optimized: false,
				zIndex: 4,
				icon: {
					url: `${this.appSettings.web.application}/assets/images/map-marker-gray.png`,
					scaledSize: new google.maps.Size(28, 40),
					size: new google.maps.Size(28, 40)
				}
			}),
			...this.schools
				.map((school) => {
					if (!school.latitude || !school.longitude) {
						return null;
					}

					const marker = new google.maps.Marker({
						position: {
							lat: school.latitude,
							lng: school.longitude
						},
						zIndex: 9999999999,
						map: this.googleMap,
						optimized: false,
						title: school.schoolName,
						icon: <google.maps.ReadonlyIcon>{
							url: `${this.appSettings.web.application}/assets/images/map-marker-orange.png`,
							scaledSize: new google.maps.Size(28, 40),
							size: new google.maps.Size(28, 40)
						}
					});

					marker.addListener('click', () => {
						if (this.selectedSchoolID === school.schoolID) {
							this.store.dispatch(new NearbySchoolsActions.SelectNearbySchool(null));
						} else {
							this.store.dispatch(new NearbySchoolsActions.SelectNearbySchool(school.schoolID));
						}
					});

					return marker;
				})
				.filter((item) => item != null)
		];

		this.updateMarkerIcons(this.selectedSchoolID);
	}

	setBounds() {
		const bounds = new google.maps.LatLngBounds();

		if (this.guardianHouseholdLatLng) {
			bounds.extend(this.guardianHouseholdLatLng);
		}

		this.mapMarkers.forEach((marker) => bounds.extend(marker.getPosition()));

		this.googleMap.fitBounds(bounds);
	}

	onMarkerSelect(schoolID: number) {
		this.updateMarkerIcons(schoolID);
		this.showPopUp(schoolID);
	}

	updateMarkerIcons(schoolID: number) {
		const school = this.schools.find((s) => s.schoolID === schoolID);
		const appliedSchools = this.schools.filter((s) => s.isApplying);
		const schoolMarker = this.mapMarkers.find((mm) => mm.getTitle() && mm.getTitle() === school?.schoolName);

		this.mapMarkers.forEach((marker) => {
			if (marker.getTitle()) {
				let iconPath;

				if (appliedSchools.some((as) => as.schoolName === marker.getTitle())) {
					iconPath = `${this.appSettings.web.application}/assets/images/map-marker-green.png`;
				} else {
					iconPath = `${this.appSettings.web.application}/assets/images/map-marker-orange.png`;
				}

				marker.setIcon({
					url: iconPath,
					scaledSize: new google.maps.Size(28, 40),
					size: new google.maps.Size(28, 40)
				});
			}
		});

		if (schoolMarker) {
			schoolMarker.setIcon({
				url: `${this.appSettings.web.application}/assets/images/map-marker-blue.png`,
				scaledSize: new google.maps.Size(28, 40),
				size: new google.maps.Size(28, 40)
			});
		}
	}

	showPopUp(schoolID: number) {
		this.currentPopup?.onRemove();

		const school = this.schools.find((s) => s.schoolID === schoolID);
		const schoolMarker = this.mapMarkers.find((mm) => mm.getTitle() === school?.schoolName);

		if (!school || !schoolMarker) {
			return;
		}

		let popupContent = `
    <button class="popup-close-btn"><span class="fa fa-close"></span></button>`;

		if (!school.isApplying) {
			popupContent += `
      <h6>${school.schoolName}</h6>
      <small>${school.address}, ${school.city}</small>
      <div class="nha-text-center">
        <button class="nha-btn nha-btn-secondary add-btn">
        <span class="fa fa-plus-circle"></span> ${this.appContent.resourceStrings['nha.sa.addtoapplyadditionalschool']}</button>
      </div>`;
		} else {
			popupContent += `
      <h6>${school.schoolName}</h6>
      <small>${school.address}, ${school.city}</small>
      <h5><span class="fa fa-check"></span> ${this.appContent.resourceStrings['nha.sa.applyingconfirmationcheckmark']}</h5>`;
		}

		this.currentPopup = new GMapsCustomPopup(schoolMarker.getPosition(), popupContent);
		this.currentPopup.setMap(this.googleMap);

		if (this.currentPopupEventSub) {
			this.currentPopupEventSub.unsubscribe();
		}

		this.currentPopupEventSub = this.currentPopup.getMouseEvents().subscribe((event) => {
			if ((event.target as HTMLElement).classList.contains('add-btn')) {
				this.addedToApplication.emit(this.selectedSchoolID);
			}

			if ((event.target as HTMLElement).classList.contains('popup-close-btn') || (event.target as HTMLElement).classList.contains('fa-close')) {
				this.store.dispatch(new NearbySchoolsActions.SelectNearbySchool(null));
				this.currentPopup.onRemove();
				this.currentPopupEventSub.unsubscribe();
			}
		});
	}

	panToSchool(schoolID: number) {
		const school = this.schools.find((s) => s.schoolID === schoolID);
		const schoolMarker = this.mapMarkers.find((mm) => mm.getTitle() === school.schoolName);

		this.googleMap.panTo(schoolMarker.getPosition());
	}
}
